import { UserAddOutlined } from '@ant-design/icons';
import { Button, Input, Modal } from 'antd';
import InvitePlayerToSeasonDialog from 'components/InvitePlayerToSeasonDialog';
import RelationsTable from 'components/RelationsTable';
import useEntities from 'hooks/useEntities';
import useFetchEntities from 'hooks/useFetchEntities';
import useOrganization from 'hooks/useOrganization';
import React, { useState } from 'react';
import useLoginStore from 'store/loginStore';
import useNotificationStore from 'store/notificationStore';
import { patchEntity } from 'utils/apiFunctions';
import icons from 'utils/icons';
import { useRemovePlayerRegistrationModal } from 'utils/useRemovePlayerRegistrationModal';

const PlayerSeasonPlayerRegistrations = ({ playerId, playerData, onSeasonRegistrationChange, onPlayerDataReload }) => {
  const entities = useEntities();
  const targetEntity = entities.SeasonPlayerRegistration;
  const { organization } = useOrganization();
  const isAdmin = useLoginStore(s => s.isAdmin)();
  const isTeamManager = useLoginStore(s => s.isTeamManager)();
  const currentRole = useLoginStore(s => s.getCurrentRole)();
  const [inviteToSeasonOpen, setInviteToSeasonOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [updatedPlayerEmail, setUpdatedPlayerEmail] = useState(null);
  const pendingInviteOpenRef = React.useRef(false);
  const addMessage = useNotificationStore((state) => state.addMessage);
  const { showRemovePlayerSeasonRegistrationWithConfirm } = useRemovePlayerRegistrationModal();
  const ORGANIZATION_ID = organization?.organizationId;
  
  // Fetch all registrations for this player
  const { data, isLoading, reload } = useFetchEntities('SeasonPlayerRegistration', {
    filters: { playerId },
    relations: 'Season(name,playerRegFeeMandatory,playerRegFeeName,playerRegFeeDescription),Season>Competition(competitionId,name,logoUrl)',
  });

  // Handle successful invitation
  const handleInviteSuccess = () => {
    // Reload data after successful invitation with a small delay to ensure server processing
    setTimeout(() => {
      reload();
      // Now it's safe to call onSeasonRegistrationChange as the invite dialog is closing
      onSeasonRegistrationChange && onSeasonRegistrationChange();
    }, 500); // 500ms delay to ensure server has processed the changes
  };

  // Handler for when email dialog fully closes
  const handleEmailDialogAfterClose = () => {
    if (pendingInviteOpenRef.current) {
      pendingInviteOpenRef.current = false;
      // Open invite dialog after email dialog is fully closed
      // Using multiple layers of timing to ensure it works even if component re-renders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setInviteToSeasonOpen(true);
          }, 200);
        });
      });
    }
  };


  const handleRemoveRegistration = (record) => {
    showRemovePlayerSeasonRegistrationWithConfirm(record, () => {
      reload();
    }, currentRole);
  };

  const handleInviteToSeasonClick = () => {
    // For team managers, always show email dialog (even if email exists) to allow editing
    if (isTeamManager) {
      // Prefill with existing email if available
      setEmailValue(playerData?.email || '');
      setEmailDialogOpen(true);
    } else {
      // Continue with normal flow (admin with existing email)
      setInviteToSeasonOpen(true);
    }
  };

  const handleSaveEmail = async () => {
    // Validate email
    if (!emailValue || !emailValue.includes('@')) {
      addMessage({ text: 'Please enter a valid email address.', type: 'error' });
      return;
    }

    const originalEmail = playerData?.email || '';
    const emailChanged = emailValue.trim() !== originalEmail.trim();

    // If email hasn't changed, just proceed to invite dialog without saving
    if (!emailChanged) {
      // Store the email to pass to the invite dialog (use existing from playerData)
      setUpdatedPlayerEmail(null); // No update needed, use original
      // Set flag to open invite dialog after email dialog closes
      pendingInviteOpenRef.current = true;
      // Close email dialog - the afterClose callback will handle opening the invite dialog
      setEmailDialogOpen(false);
      return;
    }

    // Email has changed, save it
    setSavingEmail(true);
    try {
      const response = await patchEntity(
        ORGANIZATION_ID,
        entities.Player,
        playerId,
        { email: emailValue },
        currentRole
      );

      if (response.status === 200) {
        addMessage({ text: 'Email address saved successfully.', type: 'success' });
        // Store the updated email to pass to the invite dialog
        setUpdatedPlayerEmail(emailValue);
        // Set flag to open invite dialog after email dialog closes
        pendingInviteOpenRef.current = true;
        // Close email dialog - the afterClose callback will handle opening the invite dialog
        setEmailDialogOpen(false);
        // Reload player data in background (this won't cause remount since it doesn't change the key)
        // Don't call onSeasonRegistrationChange here as it changes the key and remounts the component
        if (onPlayerDataReload) {
          // Delay to avoid interfering with dialog opening
          setTimeout(() => {
            onPlayerDataReload();
          }, 1000);
        }
      } else {
        const errorMessage = response.body?.message || 'Failed to save email address.';
        addMessage({ text: errorMessage, type: 'error' });
      }
    } catch (error) {
      addMessage({ text: 'Error saving email address.', type: 'error' });
    } finally {
      setSavingEmail(false);
    }
  };

  // Columns: all from SeasonPlayerRegistrations.js except Player and Action
  const columns = [
    'Competition.Name', 
    'Season.Name',
    'PaymentStatus',
    'PaymentMethod',
    'RegistrationStatus',
    // 'CreatedAt',
    // ...(isAdmin ? ['UpdatedBy', 'UpdatedAt'] : []),
    ...(isAdmin ? [{
      title: '',
      sortDirections: [],
      width: 50,
      className: 'pointer-events-none',
      // fixed: !isMobile ? 'right' : undefined,
      exportable: false,
      render: (record) =>
        <span className="flex flex-nowrap gap-4 pointer-events-none">
            <button className="flex items-center gap-1 pointer-events-auto hover:text-red-500 text-left" 
              onClick={(e) => { e.stopPropagation(); handleRemoveRegistration(record); }}
            >
              <span className="flex items-center gap-1">
                {icons.Trash}
                Remove
              </span>
            </button>
        </span>
    }] : [])
  ];

  return (
    <>
      <RelationsTable
        data={data}
        sourceEntity={entities.Player}
        targetEntity={targetEntity}
        columns={columns}
        emptyMessage={isLoading ? 'Loading...' : 'No season registrations found for this player.'}
        rowClickable={false}
        buttons={[
          (isAdmin || isTeamManager) && organization?.seasonPlayerRegistrationEnabled && {
            label: 'Invite to Season',
            icon: <UserAddOutlined />,
            onClick: handleInviteToSeasonClick,
            type: 'primary',
          }
        ].filter(Boolean)}
      />
      <InvitePlayerToSeasonDialog
        open={inviteToSeasonOpen}
        onClose={() => { 
          setInviteToSeasonOpen(false);
          setUpdatedPlayerEmail(null);
        }}
        player={updatedPlayerEmail ? { ...playerData, email: updatedPlayerEmail } : playerData}
        onSuccess={handleInviteSuccess}
      />
      <Modal
        open={emailDialogOpen}
        onCancel={() => {
          pendingInviteOpenRef.current = false;
          setEmailDialogOpen(false);
        }}
        afterClose={handleEmailDialogAfterClose}
        destroyOnClose={true}
        title={playerData?.email ? "Player Email Address" : "Email Address Required"}
        footer={[
          <Button key="cancel" onClick={() => {
            pendingInviteOpenRef.current = false;
            setEmailDialogOpen(false);
          }}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={savingEmail}
            onClick={handleSaveEmail}
          >
            { playerData?.email ? "Continue" : "Save"}
          </Button>,
        ]}
      >
        <div className="mb-4">
          <p className="mb-4">
            { playerData?.email
              ? "A valid email address is required to invite a player to a season. You can update the email address if needed."
              : "A valid email address is required to invite a player to a season. Please enter an email address for this player."
            }
          </p>
          {playerData?.userId && (
            <p className="mb-4 text-amber-600 text-sm flex items-center gap-2">
              <span>This player has a user account. Changing the email address will also update their login credentials.</span>
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Input
              type="email"
              placeholder="Enter email address"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              onPressEnter={handleSaveEmail}
              autoFocus
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PlayerSeasonPlayerRegistrations; 